import { Component, EventEmitter, OnDestroy, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MasterService } from '../../services/master.service';
import { MessageService } from '../../../project-intake/services/message.service';
import { ErrorLoggerService } from '../../../shared/services/error-logger.service';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { Store, StoreUpload } from '../../models/models-vm';
import { debounceTime, distinctUntilChanged, finalize } from 'rxjs';
import { AdminConstants } from '../../constants/AdminConstants';
import * as ExcelJS from 'exceljs';
import { StoreMessageConstants } from '../../../Common/Constants/data';
import { UserLogin } from '../../../Common/models/user.login';
import { MasterFileUploadComponent } from '../master-file-upload/master-file-upload.component';

@Component({
  selector: 'app-master-stores',
  templateUrl: './master-stores.component.html',
  styleUrls: ['./master-stores.component.css', './../../../../assets/css/master_management.css']
})
export class MasterStoresComponent implements OnDestroy {

  @Output() exportComplete = new EventEmitter<void>();

  private LOADER_ID: string = 'admnstoremasterloader';

  loggedInUserID: number = 0;

  allStores: Store[] = [];
  storesList: Store[] = [];
  filteredStores: Store[] = [];
  vendorList: any[] = [];
  usaStates: any[] = [];

  chunkSize = 1000;
  currentChunkIndex = 0;
  storeForm!: FormGroup;
  currentStoreID?: number | null;
  buttonText = 'ADD STORE'; //'Add / Update';

  isActiveOptions = [
    { value: 0, label: 'Select Status' },
    { value: 1, label: 'All' },
    { value: 2, label: 'Yes' },
    { value: 3, label: 'No' },
  ];

  isFilterApplied: boolean = false;
  originalStoreList: any[] = [];
  filterForm!: FormGroup;
  appliedFilters: any = {};

  searchForm!: FormGroup;
  isTextSearchFilterApplied: boolean = false;

  @ViewChild(MasterFileUploadComponent) resetFileInput!: MasterFileUploadComponent;
  srcFileComponent: string = 'stores';
  allowedFileTypeExtensions: string = '.xlx,.xlsx';
  fileToUpload: { name: string, base64: string } | null = null;

  storeUploadErrors: any[] = [];

  constructor(
    private fb: FormBuilder,
    private masterService: MasterService,
    private msgService: MessageService,
    private errorLogService: ErrorLoggerService,
    private ngxService: NgxUiLoaderService
  ) { }

  async ngOnInit() {

    this.initForm();

    this.initFilterForm();

    this.initSearchForm();

    let encodedVal = localStorage.getItem('UserRoleInfo');
    if (encodedVal != null) {
      let userRoleInfo: UserLogin = JSON.parse(atob(encodedVal));
      this.loggedInUserID = userRoleInfo.userID;
    }

    await this.loadUSAStates();
    await this.loadVendors();
    await this.loadInitialData();

    this.searchForm.get('searchControl')?.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(searchTerm => {
        this.isTextSearchFilterApplied = true;
        this.filterStores(searchTerm);
      });
  }

  private initForm(): void {
    this.storeForm = this.fb.group({
      storeID: [null],
      vendorID: [0, [Validators.required, this.nonZeroValidator]],
      storeNumber: ['', Validators.required],
      dupStoreNumber: [''],
      tdlinx: ['', Validators.required],
      storeName: ['', Validators.required],
      dupStoreName: [''],
      street: ['', [Validators.required]],
      city: ['', [Validators.required]],
      stateCode: ['', [Validators.required]],
      zip: ['', [Validators.required]],
      phone: [''],
      isActive: [true, [Validators.required]],
      //searchControl: [''],
    });
  }

  private nonZeroValidator(control: AbstractControl) {
    return control.value !== 0 ? null : { nonZero: true };
  }

  private initFilterForm(): void {
    this.filterForm = this.fb.group({
      searchVendorID: [0],
      searchStoreNumber: [''],
      searchTdlinx: [''],
      searchStoreName: [''],
      searchCity: [''],
      searchStateCode: [[]],
      searchIsActive: ['1']
    });
  }

  private initSearchForm(): void {
    this.searchForm = this.fb.group({
      searchControl: ['']
    });
  }

  private async loadInitialData() {
    this.storesList = [];
    await this.getAllStores(true);
  }

  private async loadVendors() {
    this.masterService.getAllVendors().subscribe({
      next: (result) => (this.vendorList = result),
      error: (error) => this.msgService.htmlError('Error occurred while fetching vendors list.'),
      complete: () => { },
    });
  }

  private async loadUSAStates() {
    this.masterService.getStatesByCountry('USA').subscribe({
      next: (result) => (this.usaStates = result),
      error: (error) => this.msgService.htmlError('Error occurred while fetching states list.'),
      complete: () => { },
    });
  }

  async getAllStores(showLoader: boolean = false): Promise<void> {
    if (showLoader) this.ngxService.start(this.LOADER_ID);

    this.storesList = [];
    this.filteredStores = [];

    this.masterService.getAllStoresByVendor(0, 1).subscribe({
      next: (result) => {
        this.storesList = result;
        this.allStores = result;
        this.filteredStores = [];
        this.currentChunkIndex = 0;
        this.loadNextChunk();
      },
      error: (error) => {
        this.msgService.htmlError('Error occurred while fetching store details.');
      },
      complete: () => {
        if (showLoader) this.ngxService.stop(this.LOADER_ID);
      }
    });
  }

  loadNextChunk() {
    const nextChunk = this.getStoresChunk(this.currentChunkIndex, this.chunkSize);
    this.filteredStores = [...this.filteredStores, ...nextChunk];
    this.currentChunkIndex++;
  }

  getStoresChunk(chunkIndex: number, chunkSize: number): Store[] {
    const start = chunkIndex * chunkSize;
    const end = start + chunkSize;
    return this.allStores.slice(start, end);
  }

  onScroll() {
    if (!this.isFilterApplied && this.currentChunkIndex * this.chunkSize < this.storesList.length) {
      this.loadNextChunk();
    }
  }

  enableOrDisableStore(event: any, store: Store): void {
    store.isActive = event.target.checked;
    this.updateStore(store, 'Store updated successfully');
  }

  private updateStore(store: Store, successMessage: string): void {
    this.masterService.updateStore(store).subscribe({
      next: () => {
        this.msgService.htmlSuccess(successMessage);
        //this.resetSearchAndApplyFilter();
        this.clearFilters();
      },
      error: (err) => {
        this.msgService.htmlError('Failed to update store');
        console.error('Update error:', err);
      }
    });
  }

  // private async resetSearchAndApplyFilter() {
  //   this.searchForm.get('searchControl')?.setValue('');
  //   await this.getAllStores(true);
  // }

  onSubmit() {
    if (this.storeForm.invalid) {
      this.handleValidationErrors();
      return;
    }

    const store: Store = this.storeForm.value;
    this.ngxService.start(this.LOADER_ID);

    const saveObservable = this.currentStoreID
      ? this.masterService.updateStore(store)
      : this.masterService.addStore(this.prepareNewStore(store));

    saveObservable.subscribe({
      next: async (result) => {
        if (result === 3) {
          this.msgService.htmlInfo('A Store with either Store# or Store Name already exists.');
          this.ngxService.stop(this.LOADER_ID);
        } else {
          const successMessage = this.currentStoreID
            ? 'Store updated successfully'
            : 'Store added successfully';

          this.msgService.htmlSuccess(successMessage);
          this.clearFilters();
        }
      },
      error: (err) => {
        this.handleError('Failed to save store', err);
        this.ngxService.stop(this.LOADER_ID);
      }
    });
  }

  private handleValidationErrors(): void {
    const keyMap: any = {
      vendorID: 'Customer',
      storeNumber: 'Store Number',
      tdlinx: 'TDLINX',
      storeName: 'Store Name',
      street: 'Street',
      city: 'City',
      stateCode: 'State',
      zip: 'Zip Code',
      phone: 'Phone',
      isActive: 'Is Active'
    };

    if (this.storeForm.get('vendorID')?.value === 0) {
      this.storeForm.get('vendorID')?.setErrors({ required: true });
    }

    const errors = Object.keys(this.storeForm.controls).reduce((acc, key) => {
      const controlErrors = this.storeForm.get(key)?.errors;
      if (controlErrors) {
        if (controlErrors['required']) acc.push(`${keyMap[key]} is required.`);
        if (controlErrors['pattern']) acc.push(`${key} must be a valid number.`);
      }
      return acc;
    }, [] as string[]);

    if (errors.length) {
      this.msgService.htmlError(errors.join('<br/>'));
    }
  }

  private prepareNewStore(store: Store): Store {
    return {
      ...store,
      isActive: true
    };
  }

  editStore(storeId: number): void {
    this.currentStoreID = storeId;
    const store = this.storesList.find((p) => p.storeID === storeId);
    if (store) {
      this.storeForm.patchValue(store);
      this.buttonText = 'UPDATE STORE'; //'Add / Update';
    }
  }

  async clearFilters() {
    this.resetStoreForm();
    this.resetSearchForm();
    this.resetFilterForm();

    await this.loadInitialData();
  }

  resetStoreForm() {
    this.storeForm.reset({
      storeID: null,
      vendorID: 0,
      storeNumber: '',
      tdlinx: '',
      storeName: '',
      street: '',
      city: '',
      stateCode: '',
      zip: '',
      phone: '',
      isActive: true
    });
    this.currentStoreID = null;
    this.buttonText = 'ADD STORE'; //'Add / Update';
  }

  resetSearchForm(): void {
    this.searchForm.patchValue({ searchControl: '' });
    this.isTextSearchFilterApplied = false;
  }

  resetFilterForm() {
    this.filterForm.reset(); // clears the form
    this.appliedFilters = {}; // clear UI summary of filters
    this.isFilterApplied = false;

    this.filterForm.patchValue({
      searchVendorID: 0,
      searchStoreNumber: '',
      searchTdlinx: '',
      searchStoreName: '',
      searchCity: '',
      searchStateCode: [],
      searchIsActive: "1"
    });

    this.filteredStores = [];
  }

  ngOnDestroy() {
    this.storesList = [];
  }

  private handleError(message: string, error: any): void {
    this.msgService.htmlError(message);
    this.errorLogService.logErrorNew(message, error);
  }

  onApplyFilter(): void {
    const filters = this.filterForm.value;

    const isAnyFilterSelected =
      (filters.searchVendorID && filters.searchVendorID !== 0) ||
      (filters.searchStoreNumber && filters.searchStoreNumber.trim() !== '') ||
      (filters.searchTdlinx && filters.searchTdlinx.trim() !== '') ||
      (filters.searchStoreName && filters.searchStoreName.trim() !== '') ||
      (filters.searchCity && filters.searchCity.trim() !== '') ||
      (filters.searchStateCode && filters.searchStateCode.length > 0) ||
      (filters.searchIsActive && filters.searchIsActive !== "0");

    if (!isAnyFilterSelected) {
      this.msgService.htmlWarning('Please select at least one filter option.');
      return;
    }

    // Save readable filters (unchanged)
    this.appliedFilters = {
      vendorName: filters.searchVendorID
        ? this.vendorList.find(v => v.vendorID === +filters.searchVendorID)?.vendorName
        : '',
      storeNumber: filters.searchStoreNumber || '',
      tdlinx: filters.searchTdlinx || '',
      storeName: filters.searchStoreName || '',
      city: filters.searchCity || '',
      state: filters.searchStateCode?.length
        ? filters.searchStateCode
          .map((code: string) => this.usaStates.find(s => s.stateCode === code)?.stateName)
          .filter(Boolean)
          .join(', ')
        : '',
      isActive: this.getIsActiveLabel(filters.searchIsActive),
    };

    this.filteredStores = this.getStoresFilteredByForm();
    this.isFilterApplied = true;
  }

  private getStoresFilteredByForm(): Store[] {
    const filters = this.filterForm.value;

    const vendorID = +filters.searchVendorID;
    const searchStoreNumber = filters.searchStoreNumber?.toLowerCase();
    const searchTdlinx = filters.searchTdlinx?.toLowerCase();
    const searchStoreName = filters.searchStoreName?.toLowerCase();
    const searchCity = filters.searchCity?.toLowerCase();
    const searchStateCode = filters.searchStateCode;
    const searchIsActive = filters.searchIsActive;

    return this.storesList.filter(store => {
      const matchVendor = vendorID === 0 || store.vendorID === vendorID;
      //const matchStoreNumber = !searchStoreNumber || store.storeNumber?.toLowerCase().includes(searchStoreNumber);
      //const matchTdlinx = !searchTdlinx || store.tdlinx?.toLowerCase().includes(searchTdlinx);
      const matchStoreNumber = !searchStoreNumber || store.storeNumber?.toLowerCase() === searchStoreNumber;
      const matchTdlinx = !searchTdlinx || store.tdlinx?.toLowerCase() === searchTdlinx;
      const matchStoreName = !searchStoreName || store.storeName?.toLowerCase().includes(searchStoreName);
      const matchCity = !searchCity || store.city?.toLowerCase().includes(searchCity);
      const matchState = !searchStateCode || searchStateCode.length === 0 || searchStateCode.includes(store.stateCode);
      const matchIsActive =
        !searchIsActive || searchIsActive === "0" || searchIsActive === "1" ||
        (searchIsActive === "2" && store.isActive === true) ||
        (searchIsActive === "3" && store.isActive === false);

      return (
        matchVendor &&
        matchStoreNumber &&
        matchTdlinx &&
        matchStoreName &&
        matchCity &&
        matchState &&
        matchIsActive
      );
    });
  }

  getIsActiveLabel(value: string): string {
    switch (value) {
      case "1":
        return 'All';
      case "2":
        return 'Yes';
      case "3":
        return 'No';
      default:
        return '';
    }
  }

  private filterStores(searchTerm: string): void {
    const search = searchTerm?.trim().toLowerCase();

    const searchableFields: (keyof Store)[] = [
      'tdlinx', 'storeName', 'storeNumber', 'street',
      'city', 'state', 'zip', 'phone'
    ];

    if (!search) {
      this.isTextSearchFilterApplied = false;

      if (this.isFilterApplied) {
        this.filteredStores = this.getStoresFilteredByForm();
      } else {
        this.filteredStores = [...this.storesList];
      }
      return;
    }

    // Always start from a fresh filtered base
    const baseList = this.isFilterApplied ? this.getStoresFilteredByForm() : this.storesList;

    this.filteredStores = baseList.filter(store =>
      searchableFields.some(field =>
        (store[field] as string | undefined)?.toLowerCase().includes(search)
      )
    );

    this.isTextSearchFilterApplied = true;
  }

  onFilesChanged(files: Array<{ name: string, base64: string }>) {
    this.fileToUpload = null;
    if (files[0].name !== 'Invalid' && files[0].base64 != '') {
      this.fileToUpload = files[0];
    }
    else {
      this.msgService.htmlError(StoreMessageConstants.STORE_INVALID_EXCEL);
    }
  }

  storeBulkUpload() {
    // Check if file data is available
    if (this.fileToUpload) {
      this.uploadStores(this.fileToUpload.name, this.fileToUpload.base64);
    } else {
      this.msgService.htmlError('No file selected or invalid file.'); // Show error if no file selected
    }
  }

  uploadStores(fileName: string, base64: string) {
    const objStores = new StoreUpload();
    objStores.DocumentObj = base64.substr(base64.indexOf(',') + 1);
    objStores.DocumentName = fileName;
    objStores.CreatedBy = this.loggedInUserID;

    this.ngxService.start();

    this.masterService.UploadStores(objStores)
      .pipe(finalize(() => this.ngxService.stop()))
      .subscribe({
        next: (res) => {
          this.storeUploadErrors = res;
          this.resetFileInput.resetFileInput();

          if (this.storeUploadErrors && this.storeUploadErrors.length > 0) {
            this.msgService.htmlSuccess(StoreMessageConstants.STORE_PARTIAL_UPLOAD_SUCCESS);
            this.exportErrorsToExcel();
          } else {
            this.msgService.htmlSuccess(StoreMessageConstants.STORE_UPLOAD_SUCCESS);
          }

          this.fileToUpload = null;
          this.loadInitialData();
        },
        error: (err) => {
          this.msgService.htmlError("Store upload failed. Please try again.");
          console.error("Upload error:", err);
        }
      });
  }

  // Method to download sample Excel file
  downloadSampleExcel() {
    // Create a new workbook and worksheet
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Export');

    // Define the column headers with keys
    worksheet.columns = [
      { header: 'CUSTOMER', key: 'CustomerName', width: 15 },
      { header: 'STORE NUMBER', key: 'StoreNumber', width: 20 },
      { header: 'TDLINX', key: 'TDLINX', width: 20 },
      { header: 'STORE NAME', key: 'StoreName', width: 20 },
      { header: 'STREET', key: 'Street', width: 30 },
      { header: 'CITY', key: 'City', width: 30 },
      { header: 'STATE', key: 'State', width: 10 },
      { header: 'ZIP CODE', key: 'ZipCode', width: 30 },
      { header: 'PHONE', key: 'Phone', width: 30 }
    ];

    worksheet.getRow(1).eachCell((cell) => {
      cell.font = { bold: true, size: 11 };
      cell.alignment = { vertical: 'middle', horizontal: 'left' };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFD3D3D3' } // light gray background
      };
    });

    // Optionally add a sample row to help users understand the format
    worksheet.addRow({
      CustomerName: 'Walmart',
      StoreNumber: '000001',
      TDLINX: '377057',
      StoreName: 'ROGERS, AR Sun',
      Street: '2110 W WALNUT ST',
      City: 'ROGERS',
      State: 'Arkansas',
      ZipCode: '72756-3246',
      Phone: ''
    });

    worksheet.addRow({
      CustomerName: 'Walmart',
      StoreNumber: '000002',
      TDLINX: '376997',
      StoreName: 'HARRISON AR',
      Street: '161 N WALMART DR',
      City: 'HARRISON',
      State: 'Arkansas',
      ZipCode: '72601-1960',
      Phone: ''
    });

    // Write the workbook to a buffer and trigger download
    workbook.xlsx.writeBuffer().then((buffer: any) => {
      if (buffer) {
        // Create a Blob from the buffer
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        this.downloadFile(blob, 'Store_Upload_Template.xlsx');
      } else {
        console.error("Failed to generate Excel buffer for sample download");
      }
    }).catch((err: any) => {
      console.error("Error writing sample Excel file: ", err);
    });
  }

  exportErrorsToExcel() {
    if (this.storeUploadErrors && this.storeUploadErrors.length > 0) {
      // Create a new workbook and worksheet
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Upload Errors');

      // Add column headers to the worksheet
      worksheet.columns = [
        { header: 'Vendor Name', key: 'vendorName', width: 30 },
        { header: 'Store Number', key: 'storeNumber', width: 20 },
        { header: 'Error Message', key: 'errorMessage', width: 32 }
      ];

      worksheet.getRow(1).eachCell((cell) => {
        cell.font = { bold: true, size: 11 };
        cell.alignment = { vertical: 'middle', horizontal: 'left' };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFD3D3D3' } // light gray background
        };
      });

      // Add rows of errors to the worksheet
      this.storeUploadErrors.forEach(error => {
        const row = worksheet.addRow({
          vendorName: error.vendorName,
          storeNumber: error.storeNumber,
          errorMessage: error.errorMessage
        });

        //Invalid Vendor Name
        if (error.errorType === 2) {
          row.getCell(1).font = { color: { argb: 'FFFF0000' } };
        }
        //Duplicate Vendor Name & Store Number
        if (error.errorType === 4) {
          row.getCell(1).font = { color: { argb: 'FFFF0000' } };
          row.getCell(2).font = { color: { argb: 'FFFF0000' } };
        }
      });

      // Write the workbook to a buffer and trigger download
      workbook.xlsx.writeBuffer().then((buffer: any) => {
        if (buffer) {
          // Create Blob from the buffer
          const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
          this.downloadFile(blob, 'StoreUploadErrors.xlsx');
        } else {
          console.error("Failed to generate Excel buffer");
        }
      }).catch((err: any) => {
        console.error("Error writing Excel file: ", err);
      });
    } else {
      this.msgService.htmlError("No errors to export.");
    }
  }

  exportStoresList() {
    if (this.filteredStores.length > 0) {
      const storeKeys = this.filteredStores
        .filter(store => store.vendorID !== undefined && store.storeNumber !== undefined)
        .map(store => ({
          vendorID: store.vendorID as number,
          storeNumber: store.storeNumber as string
        }));

      this.masterService.GetStoresListReport(storeKeys, !this.isFilterApplied).subscribe({
        next: (result) => {
          this.downloadFile(result, `StoresList`);
          this.msgService.htmlSuccess(AdminConstants.EXPORT_STORESLIST_SUCCESS);
        },
        error: () => {
          this.msgService.htmlError(AdminConstants.EXPORT_STORESLIST_ERROR);
        },
        complete: () => {
          this.exportComplete.emit();
        },
      });
    } else {
      this.msgService.htmlError(AdminConstants.EXPORT_STORESLIST_NOTAVAILABLE);
      this.exportComplete.emit();
    }
  }

  downloadFile(blob: Blob, filename: string): void {
    if (blob) {
      const a = document.createElement('a');
      const objectUrl = URL.createObjectURL(blob);
      a.href = objectUrl;

      const now = new Date();
      let reportFileName = filename;
      a.download = `${reportFileName}.xlsx`;
      a.click();
      URL.revokeObjectURL(objectUrl);
    } else {
      this.msgService.htmlError(AdminConstants.EXPORT_USERSLIST_DOWNLOADERROR);
    }
  }

  stopPropagation(event: Event) {
    event.stopPropagation();
  }

  getLimitedStates(stateNames: string, limit: number): string {
    if (!stateNames) return '';

    const statesArray = stateNames.split(',').map(s => s.trim());

    if (statesArray.length <= limit) {
      return statesArray.join(', ');
    }

    const limited = statesArray.slice(0, limit).join(', ');
    const remaining = statesArray.length - limit;

    return `${limited} +${remaining} more`;
  }

}